import React, { useState } from 'react';
import Table, { TableColumn } from './Table';
import styles from './PipelineStatusTable.module.css';
import { IPipelineStatus } from '../types/dashboard';

function getLast7Days(): string[] {
  const days: string[] = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days.reverse();
}

const last7Days = getLast7Days();

function Tooltip({ children, x, y }: { children: React.ReactNode; x: number; y: number }) {
  return (
    <div className={styles['pipeline-tooltip']} style={{ left: x, top: y }}>
      {children}
    </div>
  );
}

interface PipelineStatusTableProps {
  data: IPipelineStatus[];
}

const PipelineStatusTable: React.FC<PipelineStatusTableProps> = ({ data }) => {
  const [tooltip, setTooltip] = useState<null | { x: number; y: number; content: React.ReactNode }>(null);

  const tableRows = [...data].map((pipeline) => {
    const row: Record<string, unknown> = { pipeline: pipeline.pipeline_name || '', description: pipeline.description || '' };
    last7Days.forEach((date) => {
      const runsForDay = pipeline.runs.filter((r) => new Date(r.date).toISOString().slice(0, 10) === date);
      const run = runsForDay.length ? runsForDay.reduce((latest, r) => (r.date > latest.date ? r : latest)) : undefined;
      row[date] = run ? run.termination_details.code : undefined;
      row[`${date}_type`] = run ? run.termination_details.type : undefined;
      row[`${date}_message`] = run ? run.termination_details.message : undefined;
      row[`${date}_duration`] = run ? run.duration_seconds : undefined;
    });
    return row;
  });

  const pipelineStatusColumns: TableColumn<Record<string, unknown>>[] = [
    {
      key: 'pipeline',
      label: 'Pipeline',
      align: 'left',
      render: (value: unknown, row: Record<string, unknown>) => {
        const pipelineName = value as string;
        const description = row.description as string;
        const handleMouseEnter = (e: React.MouseEvent) => {
          setTooltip({
            x: e.clientX + 12,
            y: e.clientY,
            content: <div style={{ minWidth: 200 }}>{description || 'No description available'}</div>,
          });
        };
        const handleMouseLeave = () => setTooltip(null);
        return (
          <span
            style={{ position: 'relative', cursor: 'pointer', display: 'inline-block' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {pipelineName}
          </span>
        );
      },
    },
    ...last7Days.map((date) => ({
      key: date,
      label: date,
      align: 'center' as const,
      render: (value: unknown, row: Record<string, unknown>) => {
        const statusValue = value as string | undefined;
        const code = row[date] as string | undefined;
        const type = row[`${date}_type`] as string | undefined;
        const message = row[`${date}_message`] as string | undefined;
        const duration = row[`${date}_duration`] as number | undefined;
        const hasTooltip = code || type || message || duration;
        const content = (
          <div>
            {duration !== undefined && (
              <div>
                <b>Duration:</b> {duration} sec
              </div>
            )}
            {code && (
              <div style={{ marginTop: 4 }}>
                <b>Code:</b> {code}
              </div>
            )}
            {type && (
              <div style={{ marginTop: 4 }}>
                <b>Type:</b> {type}
              </div>
            )}
            {message && (
              <div style={{ marginTop: 4 }}>
                <b>Details:</b> {message}
              </div>
            )}
          </div>
        );
        const handleMouseEnter = (e: React.MouseEvent) => {
          if (hasTooltip) {
            setTooltip({
              x: e.clientX + 12,
              y: e.clientY + 12,
              content,
            });
          }
        };
        const handleMouseLeave = () => setTooltip(null);
        const icon =
          statusValue === 'SUCCESS' ? (
            <span
              data-testid="pipeline-status-green"
              className="inline-flex w-5 h-5 bg-green-500 rounded-full items-center justify-center mx-auto"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 8l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          ) : statusValue === 'SUCCESS_WITH_FAILURES' ? (
            <span
              data-testid="pipeline-status-amber"
              className="inline-flex w-5 h-5 bg-amber-400 rounded-full items-center justify-center mx-auto"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="#fff" strokeWidth="2" fill="#fbbf24" />
              </svg>
            </span>
          ) : statusValue === 'SKIPPED' || (typeof statusValue === 'string' && statusValue.includes('CANCELED')) ? (
            <span
              data-testid="pipeline-status-grey"
              className="inline-flex w-5 h-5 bg-gray-400 rounded-full items-center justify-center mx-auto"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="6" stroke="#fff" strokeWidth="2" fill="#9CA3AF" />
              </svg>
            </span>
          ) : statusValue ? (
            <span
              data-testid="pipeline-status-red"
              className="inline-flex w-5 h-5 bg-red-500 rounded-full items-center justify-center mx-auto"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M5 5l6 6M11 5l-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          ) : (
            <span></span>
          );
        return hasTooltip ? (
          <span onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ cursor: 'pointer', display: 'inline-block' }}>
            {icon}
          </span>
        ) : (
          icon
        );
      },
    })),
  ];

  return (
    <div className="bg-white rounded-lg p-6 max-w-full overflow-x-auto" style={{ position: 'relative' }}>
      <h3 className="text-lg font-bold mb-4">Data Pipeline Status</h3>
      {tableRows.length === 0 ? (
        <div className="text-gray-500 py-8 text-center">No data to display</div>
      ) : (
        <Table<Record<string, unknown>> columns={pipelineStatusColumns} data={tableRows} />
      )}
      {tooltip && (
        <Tooltip x={tooltip.x} y={tooltip.y}>
          {tooltip.content}
        </Tooltip>
      )}
    </div>
  );
};

export default PipelineStatusTable;
